import { refreshAccessToken } from "@/backend/api/auth/refresh";
import { parseJwt } from "@/features/security/utils/jwt-parser";
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
  type AxiosRequestHeaders,
} from "axios";
import { toast } from "sonner";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(null);
    }
  });
  failedQueue = [];
};

class HttpClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true, // Include cookies in requests
    });

    // Request interceptor - no longer managing tokens in localStorage
    // JWT is automatically sent via httpOnly cookie
    this.instance.interceptors.request.use(
      (config) => {
        // Cookies are automatically included with withCredentials: true.
        const platformToken = localStorage.getItem("auth_token");
        const idpToken = localStorage.getItem("idp_token");

        if (idpToken && !config.headers?.Authorization) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${idpToken}`,
          } as AxiosRequestHeaders;
        }

        config.headers = {
          ...config.headers,
          "x-client-id": "11111111-1111-1111-1111-111111111111",
        } as unknown as AxiosRequestHeaders;

        if (platformToken) {
          if (!config.headers?.["x-platform-token"]) {
            config.headers = {
              ...config.headers,
              "x-platform-token": platformToken,
            } as unknown as AxiosRequestHeaders;
          }

          // Automatically extract and set tenant and client_id from platform token
          const payload = parseJwt(platformToken);
          if (payload) {
            if (payload.tenant && !config.headers?.["x-tenant"]) {
              config.headers = {
                ...config.headers,
                "x-tenant": payload.tenant,
              } as unknown as AxiosRequestHeaders;
            }
            if (payload.aud && !config.headers?.["x-client-id"]) {
              config.headers = {
                ...config.headers,
                "x-client-id": payload.aud,
              } as unknown as AxiosRequestHeaders;
            }
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor for error handling
    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        if (!originalRequest) {
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized - Token expired or invalid
        if (error.response?.status === 401 && !originalRequest._retry) {
          const requestUrl = originalRequest.url || "";

          // A failing refresh means the session has ended (inactivity or max
          // age); don't queue behind our own refresh, just surface the error.
          if (requestUrl.includes("/auth/tokens/refresh")) {
            return Promise.reject(error);
          }

          // ── All routes, including mgmt — attempt token refresh ──
          // The refresh renews both the platform token and the Entra token,
          // so an active user is never signed out by the token lifetime.
          if (isRefreshing) {
            // Wait for the current refresh to complete
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.instance.request(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const staleIdpToken = localStorage.getItem("idp_token");
          const stalePlatformToken = localStorage.getItem("auth_token");

          try {
            // Attempt to refresh the platform token
            await refreshAccessToken();
            // Token refreshed successfully (new tokens stored by refreshAccessToken)
            processQueue();
            isRefreshing = false;

            // The request still carries the expired tokens; swap them for the
            // renewed ones (but leave headers the caller set explicitly alone).
            const headers = originalRequest.headers as any;
            const newIdpToken = localStorage.getItem("idp_token");
            const newPlatformToken = localStorage.getItem("auth_token");
            const read = (name: string) =>
              headers?.get ? headers.get(name) : headers?.[name];
            const write = (name: string, value: string) => {
              if (headers?.set) headers.set(name, value);
              else if (headers) headers[name] = value;
            };
            if (
              staleIdpToken &&
              newIdpToken &&
              read("Authorization") === `Bearer ${staleIdpToken}`
            ) {
              write("Authorization", `Bearer ${newIdpToken}`);
            }
            if (
              stalePlatformToken &&
              newPlatformToken &&
              read("x-platform-token") === stalePlatformToken
            ) {
              write("x-platform-token", newPlatformToken);
            }

            // Retry the original request
            return this.instance.request(originalRequest);
          } catch (refreshError) {
            // Refresh failed - redirect to user login
            processQueue(refreshError);
            isRefreshing = false;

            // Clear any client-side auth state
            if (
              !["/auth/login", "/auth/error", "/", "/login"].includes(
                window.location.pathname,
              )
            ) {
              const returnUrl = window.location.pathname + window.location.search + window.location.hash;
              try {
                sessionStorage.setItem("auth_return_url", returnUrl);
              } catch (e) {}
              window.location.href = `/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
            } else if (window.location.pathname === "/") {
              window.location.href = "/auth/login";
            }
            return Promise.reject(refreshError);
          }
        }

        // Handle 403 Forbidden - Insufficient permissions
        if (error.response?.status === 403) {
          const errorData = error.response.data as any;
          const missingPermission = errorData?.permission || errorData?.detail;

          toast.error(
            missingPermission
              ? `Permission denied: ${missingPermission}`
              : "You do not have permission to perform this action",
            {
              duration: 5000,
            },
          );
        }

        // Handle 302 (if not followed by browser or specifically returned)
        if (error.response?.status === 302) {
          const location = error.response.headers["location"] || "/login";
          window.location.href = location;
          return Promise.reject(
            new Error("Unauthorized - redirecting to login"),
          );
        }

        return Promise.reject(error);
      },
    );
  }

  public async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.request(config);
    return response.data;
  }

  public async get<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  public async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  public async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }

  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({ ...config, method: "PATCH", url, data });
  }

  // Legacy methods for backward compatibility (now deprecated)
  /** @deprecated JWT stored in httpOnly cookie - use cookies instead */
  public setToken(token: string, idpToken?: string) {
    console.warn("setToken is deprecated - JWT is stored in httpOnly cookie");
    localStorage.setItem("auth_token", token);
    if (idpToken) {
      localStorage.setItem("idp_token", idpToken);
    }
  }

  /** @deprecated JWT stored in httpOnly cookie - use cookies instead */
  public clearToken() {
    console.warn("clearToken is deprecated - JWT is stored in httpOnly cookie");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("idp_token");
  }

  /** @deprecated JWT stored in httpOnly cookie - use cookies instead */
  public getToken(): string | null {
    console.warn("getToken is deprecated - JWT is stored in httpOnly cookie");
    return localStorage.getItem("auth_token");
  }
}

export const http = new HttpClient();
export const apiClient = http;
